import { Box } from '@mui/material';
import { DeleteActionButton, EditActionButton } from '../../components/MRTDataGrid/TableActionButtons';
import { MRT_ColumnDef } from 'material-react-table';
import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useLoggedInUser } from '../../app/hooks/useLoggedInUser';
import { apiAssets, useDeleteBrandMutation } from '../../app/redux-fetch/apiAssets';
import AddButton from '../../components/Buttons/AddButton';
import ConditionalRender from '../../components/ConditionalRender/ConditionalRender';
import { useConfirmDialogContext } from '../../components/ConfirmDialog';
import MRTDataGridV2 from '../../components/MRTDataGrid/MRTDataGridv2';
import { TableTitleTypography } from '../../components/Typographies/TableTitleTypography';
import YesNoTypography from '../../components/YesNoTypograpphy/YesNoTypography';
import MakeForm from './MakeForm';
import { messages } from './messages';
import { Brand } from './types';

const MakeTable: React.FC = () => {
    const { formatMessage } = useIntl();
    const { tenantId } = useLoggedInUser();
    const { data = [], isLoading, refetch } = apiAssets.endpoints.getBrands.useQuery({ tenantId });
    const [deleteBrand] = useDeleteBrandMutation();
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [selectedBrand, setSelectedBrand] = useState<Brand | null>(null);
    const { confirm } = useConfirmDialogContext();

    const columns = useMemo<MRT_ColumnDef<Brand>[]>(
        () => [
            {
                accessorKey: 'name',
                header: formatMessage(messages.name),
            },
            {
                accessorKey: 'systemDefined',
                header: formatMessage(messages.systemDefined),
                Cell: ({ cell }: any) => <YesNoTypography isYes={cell.getValue()} />,
            },
            {
                accessorKey: 'inUse',
                header: formatMessage(messages.inUse),
                Cell: ({ cell }: any) => <YesNoTypography isYes={cell.getValue()} />,
            },
        ],
        [formatMessage]
    );

    const handleDelete = async (id: number) => {
        const confirmed = await confirm({
            title: formatMessage(messages.deleteMakeTitle),
            message: formatMessage(messages.deleteDescription),
            confirmText: formatMessage(messages.deleteBtn),
            cancelText: formatMessage(messages.cancel),
            severity: 'warning',
            confirmButtonColor: 'error',
            cancelButtonColor: 'secondary',
        });

        if (confirmed) {
            try {
                await deleteBrand({ tenantId, id }).unwrap();
                refetch();
            } catch (error) {
                // Error handling is done by the mutation
            }
        }
    };

    const handleOpenForm = (brand?: Brand) => {
        setSelectedBrand(brand || { id: 0, name: '', tenantId, systemDefined: false, inUse: false });
        setIsFormOpen(true);
    };

    const handleCloseForm = () => {
        setIsFormOpen(false);
        setSelectedBrand(null);
        refetch();
    };

    return (
        <>
            {selectedBrand && (
                <MakeForm
                    brand={selectedBrand}
                    isOpen={isFormOpen}
                    handleClose={handleCloseForm}
                />
            )}
            <Box sx={{ width: '100%' }}>
                <TableTitleTypography>
                    {formatMessage(messages.title)}
                </TableTitleTypography>
            </Box>

            <Box sx={{ width: '100%' }}>
                <MRTDataGridV2
                    initialState={{
                        pagination: { pageSize: 100, pageIndex: 0 },
                        density: 'comfortable',
                        sorting: [{ id: 'name', desc: false }],
                    }}
                    rightHeadingComponent={
                        <AddButton
                            text={formatMessage(messages.add)}
                            onClick={() => handleOpenForm()}
                        />
                    }
                    state={{
                        isLoading,
                    }}
                    columns={columns}
                    data={data}
                    getRowId={(row) => row.id as any}
                    enableEditing
                    enableRowActions
                    positionActionsColumn='last'
                    enableColumnActions={false}
                    enableColumnFilters={false}
                    enablePagination={true}
                    enableSorting={true}
                    enableBottomToolbar={true}
                    enableTopToolbar={false}
                    renderRowActions={({ row }) => (
                        <Box sx={{ display: 'flex' }}>
                            <EditActionButton
                                onClick={() => handleOpenForm(row.original)}
                            />
                            <ConditionalRender condition={!row.original.inUse}>
                                <DeleteActionButton
                                    onClick={() => handleDelete(row.original.id)}
                                />
                            </ConditionalRender>
                        </Box>
                    )}
                />
            </Box>
        </>
    );
};

export default MakeTable;    